
import Vue from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import { MyHotelLocalNavi } from '../-components';
import HotelViewRoot from '../../-index';
import {
  HSimpleCoverTitle,
  HSimpleFilterForm,
  HRestaurantList,
  HSelect,
  HCuisineContentSetList,
  HSimpleLinks,
} from '~/components';
import {
  Restaurant,
  RestaurantCommons,
  RestaurantCategory,
  RestaurantServiceTime,
  HotelCuisineContentsResponse,
  HotelDetail,
  RestaurantLayoutType,
  HOTEL_MEAL_RESTAURANT_LAYOUT_TYPES,
} from '~/schemes';
import { hour12 } from '~/helpers';

interface MyFilterConditions {
  serviceTime: RestaurantServiceTime | null;
  category: RestaurantCategory | null;
}

/**
 * 「特定の食事処のみ表示設定」（`focusedRestaurantId`） が設定されている場合の表示設定
 */
interface FocusedRestaurantSettings extends HotelCuisineContentsResponse {
  restaurant?: Restaurant;
}

/**
 * タイプ別に束ねられたレストランのリスト
 */
interface ClassedRestaurantsByType {
  layoutType: RestaurantLayoutType;
  // 既に分類されているのでタイプが混在することはない
  restaurants: Restaurant[];
}

/**
 * フィルター機能を使う食事処ー件数の条件
 */
const USE_FILTER_COUNT = 3;

@Component<MyView>({
  inject: ['hotelViewRoot'],
  async asyncData({ $commons, $hotel, params, error, $error }) {
    try {
      const { hotel_slug } = params;
      let hotel: HotelDetail | null | undefined = $hotel.current;
      if (!hotel) hotel = await $hotel.getBySlug(hotel_slug);

      // 施設情報が取得出来ない場合は404エラー
      if (!hotel)
        return error({
          statusCode: 404,
        });

      // 星のや以外は食事処が設定されていない場合は404エラー
      if (!hotel.restaurantSettings && hotel.theme !== 'hoshinoya') {
        return error({
          statusCode: 404,
        });
      }

      // 食事共通設定初期化
      let restaurantCommons: RestaurantCommons;
      /**
       * 基本的には特定の食事処と食事処に紐づく料理を設定
       * 星のやの食事ページでは食事処という概念は存在せず
       * 料理コンテンツにて食事体験を表現するため、料理のみ設定
       */
      let focusedRestaurantSettings: FocusedRestaurantSettings | null = null;

      if (hotel.theme === 'hoshinoya') {
        [restaurantCommons, focusedRestaurantSettings] = await Promise.all([
          $commons.getRestaurant(),
          $hotel.cuisinesBySlug(hotel_slug),
        ]);
      } else {
        const { focusedRestaurantId, restaurants } = hotel;

        [restaurantCommons, focusedRestaurantSettings] = await Promise.all([
          $commons.getRestaurant(),
          new Promise<FocusedRestaurantSettings | null>((resolve, reject) => {
            if (!focusedRestaurantId) return resolve(null);
            const focusedRestaurant = restaurants.find(
              (restaurant) => restaurant.id === focusedRestaurantId,
            );
            if (!focusedRestaurant) return resolve(null);

            $hotel
              .cuisinesBySlug(hotel_slug, focusedRestaurant._id)
              .then((res) => {
                resolve({
                  ...res,
                  restaurant: focusedRestaurant,
                });
              })
              .catch(reject);
          }),
        ]);
      }

      const filterConditions: MyFilterConditions = {
        serviceTime: null,
        category: null,
      };

      return {
        restaurantCommons,
        filterConditions,
        focusedRestaurantSettings,
      };
    } catch (_err) {
      $error.throw(_err);
    }
  },

  /** meta情報設定 */
  head() {
    const { hotel, restaurantSettings: settings, sectionLabel } = this;
    let description: string | undefined | null;
    let image: string | undefined;
    if (settings) {
      description = settings.meta.description;
      image = settings.meta.image;
    }
    const title = sectionLabel;
    const head: MetaInfo = {
      title,
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/dining/`,
        },
      ],
    };

    if (image)
      head.meta!.push({
        hid: 'og:image',
        property: 'og:image',
        content: this.$res.img(image),
      });

    if (description) {
      head.meta!.push(
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }
    return head;
  },
  render() {
    const {
      mainVisual,
      restaurantSettings,
      notification,
      hasRestaurants,
      hotel,
      focusedRestaurantSettings,
      sectionLabel,
      titleDeco,
    } = this;

    return (
      <div staticClass="my-hotel-dining-index-view">
        <MyHotelLocalNavi />
        <div staticClass="my-inner">
          {/**
            食事ページにおいては、タイトルコンポーネントに流すタイトル表記を画像のあり/なしで変えている
            いびつな気もするが、ライティング的に確かにこっちの方が自然だよね、とも話した
           */}
          <HSimpleCoverTitle
            title={mainVisual ? restaurantSettings!.title : sectionLabel}
            deco={titleDeco}
            image={mainVisual}
          />

          {!this.isHoshinoya() && (
            <div
              staticClass="my-head"
              class={!!mainVisual && 'my-head--has-image'}>
              <div staticClass="my-head__inner">
                {!mainVisual && (
                  <div staticClass="my-head__title">
                    {restaurantSettings!.title}
                  </div>
                )}
                <div staticClass="my-head__description">
                  <div v-wysiwyg={restaurantSettings!.description} />
                </div>
              </div>
            </div>
          )}

          <div staticClass="h-container h-container--min">
            <div staticClass="my-lead">
              {!!notification && (
                <div
                  v-wysiwyg={notification}
                  staticClass="my-lead__notification"
                />
              )}
            </div>
          </div>

          {hasRestaurants && !focusedRestaurantSettings && (
            <div>
              {this.useFilter && (
                <div staticClass="my-filter-wrapper">
                  <div staticClass="my-filter-wrapper__inner">
                    <HSimpleFilterForm
                      count={this.count}
                      scopedSlots={{
                        default: ({ renderCells }) => [
                          renderCells([
                            <HSelect
                              v-model={this.filterConditions.serviceTime}
                              label={this.$t('label.restaurantHours') as string}
                              color="default"
                              items={this.serviceTimeOptions.map(
                                ({ name, value }) => ({
                                  label: name,
                                  value,
                                }),
                              )}
                            />,
                            <HSelect
                              v-model={this.filterConditions.category}
                              label={this.$t('label.type') as string}
                              color="default"
                              items={this.categoryOptions.map(
                                ({ name, value }) => ({
                                  label: name,
                                  value,
                                }),
                              )}
                            />,
                          ]),
                        ],
                      }}></HSimpleFilterForm>
                  </div>
                </div>
              )}
              <div staticClass="my-contents">
                <div staticClass="my-contents__inner">
                  {/** 食事処のリスト **/}
                  {this.classedRestaurants.map((classedRestaurant) => (
                    <HRestaurantList
                      key={classedRestaurant.layoutType}
                      staticClass="my-list"
                      commons={this.restaurantCommons}
                      items={classedRestaurant.restaurants}
                      hotel={hotel}
                      dumpId
                      layoutType={classedRestaurant.layoutType}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {!!focusedRestaurantSettings && (
            <HCuisineContentSetList
              staticClass="my-cuisine-contents"
              commons={this.restaurantCommons}
              contents={focusedRestaurantSettings.cuisines}
              restaurant={focusedRestaurantSettings.restaurant}
            />
          )}
        </div>
        {/* アレルギー */}
        {!!this.hideAllergyFriendly && (
          <HSimpleLinks
            staticClass="my-allergy-links"
            items={[
              {
                key: 'allergy',
                label: this.$t('guide.allergy') as string,
                href: this.$t('url.allergy') as string,
              },
            ]}
          />
        )}
      </div>
    );
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;

  /**
   * 食事共通設定
   */
  restaurantCommons: RestaurantCommons = null as any;

  /**
   * 食事処検索条件
   */
  filterConditions: MyFilterConditions = null as any;

  /**
   * 「特定の食事処のみ表示設定」（`focusedRestaurantId`） が設定されている場合の表示設定
   *
   * * 紐づく食事処が存在しない場合はこの値も存在しない
   * * 設定された食事処に紐づく料理コンテンツが存在しない場合は、料理コンテンツ0件としてこの値は存在する
   *
   * @see {@link FocusedRestaurantSettings}
   */
  focusedRestaurantSettings: FocusedRestaurantSettings | null = null;

  /**
   * タイトルのデコレーション
   *
   * * リゾナーレの時だけ、欧文テキストを記載する
   *
   * @see https://hoshinoresorts-dev.slack.com/archives/C01PKFF4753/p1668563143395969
   */
  get titleDeco() {
    if (!this.$theme.is('risonare') || this.$language.current === 'en') {
      return;
    }
    return 'Restaurant & Cafes';
  }

  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  /**
   * テーマで設定されている食事ナビゲーション名
   */
  get sectionLabel() {
    return this.$theme.getSectionLongLabelWithoutHTML('dining');
  }

  /**
   * 施設の食事処リスト
   */
  get restaurants() {
    return this.hotel.restaurants;
  }

  /**
   * 施設が食事処を1つ以上保有しているか
   */
  get hasRestaurants() {
    return this.restaurants.length > 0;
  }

  /**
   * フィルター検索を利用するか
   *
   * @see {@link USE_FILTER_COUNT}
   */
  get useFilter() {
    return this.restaurants.length > USE_FILTER_COUNT;
  }

  /**
   * 施設の食事処設定
   */
  get restaurantSettings() {
    const { restaurantSettings } = this.hotel;
    if (!restaurantSettings && !this.isHoshinoya()) {
      throw new Error('missing restaurantSettings');
    }
    return restaurantSettings;
  }

  /**
   * 食事処のお知らせ
   */
  get notification() {
    return this.restaurantSettings && this.restaurantSettings.notification;
  }

  /**
   * アレルギー対応リンクの表示制御
   */
  get hideAllergyFriendly() {
    // CMS側で「非表示にする」にチェックを入れていたらtrueが返ってくるので反転する必要があるため!を入れています
    return (
      this.restaurantSettings && !this.restaurantSettings.canHideAllergyFriendly
    );
  }

  /**
   * 時間定義表記は将来的に施設別にカスタムできるようにする可能性がある
   * その時はあらためて設計＆実装する事
   */
  get serviceTimeOptions() {
    const { restaurants } = this;
    return [
      {
        name: this.$t('label.all') as string,
        value: null,
        from: undefined,
        to: undefined,
      },
      ...this.restaurantCommons.serviceTimeOptions,
    ]
      .filter(({ value }) => {
        return restaurants.some((r) => this.serviceTimeFilter(r, value));
      })
      .map((opt) => {
        const row = {
          name: opt.name,
          value: opt.value,
        };
        if (opt.value) {
          row.name = `${hour12(opt.from)}${this.$t('chore.marginDash')}${hour12(
            opt.to,
          )}`;
        }
        return row;
      });
  }

  /**
   * 検索フォーム用カテゴリオプションリスト
   */
  get categoryOptions() {
    const { restaurants } = this;
    return [
      {
        name: this.$t('label.all') as string,
        value: null,
      },
      ...this.restaurantCommons.categoryOptions,
    ].filter(({ value }) => {
      return restaurants.some((r) => this.categoryFilter(r, value));
    });
  }

  /**
   * 検索条件でフィルター済みの食事処リスト
   */
  get filteredRestaurants() {
    const { serviceTime, category } = this.filterConditions;
    const { restaurants } = this;

    return restaurants.filter((restaurant) => {
      return (
        this.categoryFilter(restaurant, category) &&
        this.serviceTimeFilter(restaurant, serviceTime)
      );
    });
  }

  /**
   * 検索条件でフィルター済みの食事処リストをレイアウト種別毎にマッピングしたオブジェクト
   */
  get classedRestaurants(): ClassedRestaurantsByType[] {
    const classedRestaurants: ClassedRestaurantsByType[] = [];
    const filteredRestaurants = this.filteredRestaurants;
    HOTEL_MEAL_RESTAURANT_LAYOUT_TYPES.forEach((layoutType) => {
      const restaurants = filteredRestaurants.filter((filteredRestaurant) => {
        return filteredRestaurant.layoutType === layoutType;
      });
      if (restaurants.length < 1) return;
      classedRestaurants.push({ layoutType, restaurants });
    });
    return classedRestaurants;
  }

  /**
   * フィルタ結果件数
   */
  get count() {
    return this.filteredRestaurants.length;
  }

  /**
   * メインビジュアルの設定
   */
  get mainVisual() {
    return this.restaurantSettings && this.restaurantSettings.mainVisual;
  }

  /**
   * 営業時間のフィルタ関数
   */
  serviceTimeFilter(
    restaurant: Restaurant,
    serviceTime: RestaurantServiceTime | null,
  ): boolean {
    return (
      serviceTime === null || restaurant.serviceTimes.includes(serviceTime)
    );
  }

  /**
   * カテゴリのフィルタ関数
   */
  categoryFilter(
    restaurant: Restaurant,
    category: RestaurantCategory | null,
  ): boolean {
    return category === null || restaurant.category === category;
  }

  isHoshinoya(): boolean {
    return this.$theme.is('hoshinoya');
  }
}
